import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { scoutingPlayersQueryOptions } from "@/api/scouting";
import { followedPlayerIdsQueryOptions } from "@/api/followedPlayers";
import { useScoutingContext } from "../page";
import { ScoutingPlayersTable } from "@/components/general/Scouting/ScoutingPlayersTable";
import type { StatCategory } from "@/components/general/Scouting/ScoutingPlayersTable";

export const ScoutingPlayersPage = () => {
    const {
        selectedTournamentId,
        tournaments,
        statCategory,
        searchQuery,
        positionFilter,
        setPositionFilter,
        teamFilter,
        setTeamFilter,
        setAvailablePositions,
        setAvailableTeams,
    } = useScoutingContext();
    const isTournamentsLoading = tournaments === undefined;

    const { data, isLoading } = useQuery({
        ...scoutingPlayersQueryOptions(selectedTournamentId || 0),
        enabled: !!selectedTournamentId && selectedTournamentId > 0,
    });

    const { data: followedPlayerIds } = useQuery(followedPlayerIdsQueryOptions());

    const allPlayers = data?.players || [];

    useEffect(() => {
        const teams = [...new Set(allPlayers.map((p) => p.identity.teamName).filter(Boolean))].sort();
        setAvailablePositions(["C", "LD", "LW", "RD", "RW"]);
        setAvailableTeams(teams);
    }, [data]);

    const playersWithMultiRole = useMemo(() => {
        return new Set(allPlayers.filter((p) => p.identity.role.includes("/")).map((p) => p.identity.playerId));
    }, [allPlayers]);

    const filteredPlayers = useMemo(() => {
        return allPlayers.filter((p) => {
            if (searchQuery && !p.identity.playerName.toLowerCase().includes(searchQuery.toLowerCase())) return false;
            if (teamFilter.length > 0 && !teamFilter.includes(p.identity.teamName)) return false;

            if (positionFilter.length === 0) {
                if (playersWithMultiRole.has(p.identity.playerId) && !p.identity.role.includes("/")) return false;
            } else {
                if (!positionFilter.some((pos) => p.identity.role === pos)) return false;
            }

            return true;
        });
    }, [allPlayers, searchQuery, positionFilter, teamFilter, playersWithMultiRole]);

    const playerCategory: StatCategory =
        statCategory === "overview" ||
        statCategory === "skating" ||
        statCategory === "puck" ||
        statCategory === "actions" ||
        statCategory === "passing" ||
        statCategory === "advanced"
            ? statCategory
            : "overview";

    const handleTeamClick = (team: string) => {
        setTeamFilter(teamFilter.includes(team) ? teamFilter.filter((t) => t !== team) : [...teamFilter, team]);
    };

    const handleRoleClick = (role: string) => {
        const positions = role.split("/").map((p) => p.trim());
        const allPresent = positions.every((p) => positionFilter.includes(p));
        if (allPresent) {
            setPositionFilter(positionFilter.filter((p) => !positions.includes(p)));
        } else {
            const next = [...positionFilter];
            for (const pos of positions) {
                if (!next.includes(pos)) next.push(pos);
            }
            setPositionFilter(next);
        }
    };

    return (
        <div
            id="onboarding-scouting-players"
            data-scouting-players-loaded={!isLoading ? "true" : undefined}
            className="flex flex-col rounded-md overflow-hidden border-table-border-color md:min-h-0 md:h-full"
        >
            <ScoutingPlayersTable
                data={filteredPlayers}
                isLoading={isLoading || isTournamentsLoading}
                selectedCategory={playerCategory}
                referenceData={data?.referenceData}
                onTeamClick={handleTeamClick}
                onRoleClick={handleRoleClick}
                activeTeamFilters={teamFilter}
                activeRoleFilters={positionFilter}
                followedPlayerIds={followedPlayerIds}
            />
        </div>
    );
};
